import { AuthContext, BaseNavLink, SidebarDivider, isDemoModeEnabled } from '@enter/core';
import {
    mdiCheckCircleOutline,
    mdiCheckCircle,
    mdiClipboardTextOutline,
    mdiCogOutline,
    mdiEmailOutline,
    mdiFax,
    mdiPhoneOutline,
    mdiChartLine,
    mdiFileDocumentOutline,
    mdiHandshakeOutline,
    mdiChevronUp,
    mdiChevronDown,
    mdiFlask,
    mdiFingerprint,
    mdiClipboardCheckOutline,
    mdiOpenInNew,
    mdiEyeOutline,
    mdiEyeOffOutline
} from '@mdi/js';
import Icon from '@mdi/react';
import { inject, observer } from 'mobx-react';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useMatch, useParams } from 'react-router-dom';
import { IInjectedRootStore } from 'stores';
import * as S from './main.sidebar.styles';

type IMainSidebarProps = IInjectedRootStore;

export const MainSidebar: React.FunctionComponent<IMainSidebarProps> = inject('rootStore')(
    observer(
        (props: IMainSidebarProps): React.ReactElement => {
            const { rootStore } = props;
            const { messageStore } = rootStore;
            const { getUnreadTotal, unreadInboxTotal } = messageStore;
            const context = useContext(AuthContext);
            const params = useParams();
            const accountId = params.accountId;

            const isMailRoute = useMatch(accountId ? `/${accountId}/mail/*` : 'mail/*');
            const isFaxRoute = useMatch(accountId ? `/${accountId}/fax/*` : 'fax/*');
            const isContractsRoute = useMatch(accountId ? `/${accountId}/chargemasters/*` : 'chargemasters/*');
            const isDocumentsRoute = useMatch(accountId ? `/${accountId}/documents` : 'documents');
            const isPhoneRoute = useMatch(accountId ? `/${accountId}/phone` : 'phone');
            const isFileCabinet = !!isMailRoute || !!isFaxRoute || !!isContractsRoute || !!isDocumentsRoute || !!isPhoneRoute;
            const [clickedFileCabinet, setClickedFileCabinet] = useState<boolean | null>(null);
            const showFileCabinet = clickedFileCabinet !== null ? clickedFileCabinet : isFileCabinet;
            const toggleFileCabinet = () => {
                setClickedFileCabinet((prev) => {
                    const newValue = prev === null ? !isFileCabinet : !prev;
                    return newValue;
                });
            };

            // Reset File Cabinet manual toggle when route changes
            useEffect(() => {
                setClickedFileCabinet(null);
            }, [isMailRoute, isFaxRoute, isContractsRoute, isDocumentsRoute, isPhoneRoute]);

            const isExperimentsRoute = useMatch(accountId ? `/${accountId}/verifications/experiments/*` : 'verifications/experiments/*');
            const isEvidenceRoute = useMatch(accountId ? `/${accountId}/verifications/evidence/*` : 'verifications/evidence/*');
            const isVerificationsRoute = !!isExperimentsRoute || !!isEvidenceRoute;
            const [clickedVerifications, setClickedVerifications] = useState<boolean | null>(null);
            const showVerifications = clickedVerifications !== null ? clickedVerifications : isVerificationsRoute;
            const toggleVerifications = () => {
                setClickedVerifications((prev) => {
                    const newValue = prev === null ? !isVerificationsRoute : !prev;
                    return newValue;
                });
            };

            const [showUniversityPassword, setShowUniversityPassword] = useState(false);
            const [copied, setCopied] = useState(false);
            const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
            const universityPassword = 'entertheuni';
            const copyUniversityPassword = (): void => {
                try {
                    if (navigator?.clipboard?.writeText) {
                        navigator.clipboard.writeText(universityPassword);
                        setCopied(true);
                        if (copyTimeoutRef.current) {
                            clearTimeout(copyTimeoutRef.current);
                        }
                        copyTimeoutRef.current = setTimeout(() => setCopied(false), 1500);
                        return;
                    }
                    const textarea = document.createElement('textarea');
                    textarea.value = universityPassword;
                    document.body.appendChild(textarea);
                    textarea.select();
                    document.execCommand('copy');
                    document.body.removeChild(textarea);
                    setCopied(true);
                    if (copyTimeoutRef.current) {
                        clearTimeout(copyTimeoutRef.current);
                    }
                    copyTimeoutRef.current = setTimeout(() => setCopied(false), 1500);
                } catch {
                    // ignore copy failures
                }
            };

            useEffect(
                () => () => {
                    if (copyTimeoutRef.current) {
                        clearTimeout(copyTimeoutRef.current);
                    }
                },
                [],
            );

            // Reset Verifications manual toggle when route changes
            useEffect(() => {
                setClickedVerifications(null);
            }, [isExperimentsRoute, isEvidenceRoute]);

            // NB: setup background poll to check for messages
            // No initial call - unread count is calculated from inbox messages when messaging page loads
            // Background poll updates the count when user is not on messaging page
            useEffect(() => {
                try {
                    context.cancelGroup && context.cancelGroup('messaging');
                    context.setGroup && context.setGroup('messaging');
                    const updateInterval = 3 * 60 * 1000;
                    const interval = setInterval(getUnreadTotal, updateInterval);
                    return (): void => clearInterval(interval);
                } catch (err) {
                    // bury exceptions
                }
            }, [context, getUnreadTotal]);

            const demoModeEnabled = isDemoModeEnabled();

            return (
                <S.SideBar
                    visible={!rootStore.layoutStore.sidebarCollapsed}
                    onClose={() => rootStore.layoutStore.toggleSidebar(true)}>
                    <SidebarDivider />
                    <BaseNavLink to={accountId ? `/${accountId}/claims` : 'claims'}>
                        {(linkProps): React.ReactElement => (
                            <S.StyledSidebarItem active={linkProps.active}>
                                <S.MenuIcon path={mdiClipboardTextOutline} size={1} />
                                Claims
                            </S.StyledSidebarItem>
                        )}
                    </BaseNavLink>
                    <SidebarDivider />
                    <BaseNavLink to={accountId ? `/${accountId}/messaging` : 'messaging'}>
                        {(linkProps): React.ReactElement => (
                            <S.StyledSidebarItem active={linkProps.active}>
                                <S.MenuIcon path={mdiCheckCircleOutline} size={1} />
                                Tasks{' '}
                                {unreadInboxTotal > 0 && <S.MessageCount>{unreadInboxTotal}</S.MessageCount>}
                            </S.StyledSidebarItem>
                        )}
                    </BaseNavLink>
                    <SidebarDivider />
                    <BaseNavLink to={accountId ? `/${accountId}/reports` : 'reports'}>
                        {(linkProps): React.ReactElement => (
                            <S.StyledSidebarItem active={linkProps.active}>
                                <S.MenuIcon path={mdiChartLine} size={1} />
                                Reports
                            </S.StyledSidebarItem>
                        )}
                    </BaseNavLink>
                    <SidebarDivider />
                    <BaseNavLink to={accountId ? `/${accountId}/eligibility` : 'eligibility'}>
                        {(linkProps): React.ReactElement => (
                            <S.StyledSidebarItem active={linkProps.active}>
                                <S.MenuIcon path={mdiClipboardCheckOutline} size={1} />
                                Eligibility
                            </S.StyledSidebarItem>
                        )}
                    </BaseNavLink>
                    <SidebarDivider />
                    <S.StyledSidebarItem onClick={toggleFileCabinet}>
                        <S.MenuIcon path={mdiFileDocumentOutline} size={1} />
                        <span>File Cabinet</span>
                        <S.CollapseButton 
                            variant="text"
                            onClick={(e): void => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleFileCabinet();
                            }}>
                            <S.CollapseIcon path={showFileCabinet ? mdiChevronUp : mdiChevronDown} size={1} open={showFileCabinet} />
                        </S.CollapseButton>
                    </S.StyledSidebarItem>
                    <S.SidebarCollapse in={showFileCabinet} timeout="auto" unmountOnExit={false}>
                        <BaseNavLink to={accountId ? `/${accountId}/mail/inbox` : 'mail/inbox'}>
                            {(linkProps): React.ReactElement => (
                                <S.CollapseSidebarItem active={linkProps.active}>
                                    <S.CollapseIndent />
                                    <S.SubMenuIcon path={mdiEmailOutline} size={0.9} />
                                    Mail
                                </S.CollapseSidebarItem>
                            )}
                        </BaseNavLink>
                        <BaseNavLink to={accountId ? `/${accountId}/fax/inbox` : 'fax/inbox'}>
                            {(linkProps): React.ReactElement => (
                                <S.CollapseSidebarItem active={linkProps.active}>
                                    <S.CollapseIndent />
                                    <S.SubMenuIcon path={mdiFax} size={0.9} />
                                    Fax
                                </S.CollapseSidebarItem>
                            )}
                        </BaseNavLink>
                        <BaseNavLink to={accountId ? `/${accountId}/chargemasters` : 'chargemasters'}>
                            {(linkProps): React.ReactElement => (
                                <S.CollapseSidebarItem active={linkProps.active}>
                                    <S.CollapseIndent />
                                    <S.SubMenuIcon path={mdiHandshakeOutline} size={0.9} />
                                    Contracts
                                </S.CollapseSidebarItem>
                            )}
                        </BaseNavLink>
                        <BaseNavLink to={accountId ? `/${accountId}/documents` : 'documents'}>
                            {(linkProps): React.ReactElement => (
                                <S.CollapseSidebarItem active={linkProps.active}>
                                    <S.CollapseIndent />
                                    <S.SubMenuIcon path={mdiFileDocumentOutline} size={0.9} />
                                    Documents
                                </S.CollapseSidebarItem>
                            )}
                        </BaseNavLink>
                        <BaseNavLink to={accountId ? `/${accountId}/phone` : 'phone'}>
                            {(linkProps): React.ReactElement => (
                                <S.CollapseSidebarItem active={linkProps.active}>
                                    <S.CollapseIndent />
                                    <S.SubMenuIcon path={mdiPhoneOutline} size={0.9} />
                                    Phone
                                </S.CollapseSidebarItem>
                            )}
                        </BaseNavLink>
                    </S.SidebarCollapse>
                    <SidebarDivider />
                    <S.StyledSidebarItem onClick={toggleVerifications}>
                        <S.MenuIcon path={mdiCheckCircle} size={1} />
                        <span>Verifications</span>
                        <S.CollapseButton 
                            variant="text"
                            onClick={(e): void => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleVerifications();
                            }}>
                            <S.CollapseIcon path={showVerifications ? mdiChevronUp : mdiChevronDown} size={1} open={showVerifications} />
                        </S.CollapseButton>
                    </S.StyledSidebarItem>
                    <S.SidebarCollapse in={showVerifications} timeout="auto" unmountOnExit={false}>
                        <BaseNavLink to={accountId ? `/${accountId}/verifications/experiments` : 'verifications/experiments'}>
                            {(linkProps): React.ReactElement => (
                                <S.CollapseSidebarItem active={linkProps.active}>
                                    <S.CollapseIndent />
                                    <S.SubMenuIcon path={mdiFlask} size={0.9} />
                                    Experiments
                                </S.CollapseSidebarItem>
                            )}
                        </BaseNavLink>
                        <BaseNavLink to={accountId ? `/${accountId}/verifications/evidence` : 'verifications/evidence'}>
                            {(linkProps): React.ReactElement => (
                                <S.CollapseSidebarItem active={linkProps.active}>
                                    <S.CollapseIndent />
                                    <S.SubMenuIcon path={mdiFingerprint} size={0.9} />
                                    Evidence
                                </S.CollapseSidebarItem>
                            )}
                        </BaseNavLink>
                    </S.SidebarCollapse>
                    <SidebarDivider />
                    <BaseNavLink to={accountId ? `/${accountId}/settings` : 'settings'}>
                        {(linkProps): React.ReactElement => (
                            <S.StyledSidebarItem active={linkProps.active}>
                                <S.MenuIcon path={mdiCogOutline} size={1} />
                                Settings
                            </S.StyledSidebarItem>
                        )}
                    </BaseNavLink>
                    <S.Spacer />
                    <S.ResourceLinks>
                        <S.ResourceLink
                            href="https://developers.enter.health/docs/welcome-to-enter"
                            target="_blank"
                            rel="noopener noreferrer">
                            <S.ResourceLinkText>Developer Docs</S.ResourceLinkText>
                            <S.ResourceLinkIcon path={mdiOpenInNew} size={0.65} />
                        </S.ResourceLink>
                        <S.ResourceDivider />
                        <S.ResourceLink
                            href="https://www.enter.health/university"
                            target="_blank"
                            rel="noopener noreferrer">
                            <S.ResourceLinkText>University</S.ResourceLinkText>
                            <S.ResourceLinkIcon path={mdiOpenInNew} size={0.65} />
                        </S.ResourceLink>
                        <S.PasswordRow>
                            <S.PasswordLabel>University Password:</S.PasswordLabel>
                            <S.PasswordValueRow>
                                <S.PasswordValue
                                    clickable={showUniversityPassword}
                                    role="button"
                                    tabIndex={0}
                                    title={showUniversityPassword ? 'Copy password' : 'Show password to copy'}
                                    onClick={() => {
                                        if (!showUniversityPassword) return;
                                        copyUniversityPassword();
                                    }}
                                    onKeyDown={(e): void => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            if (!showUniversityPassword) return;
                                            copyUniversityPassword();
                                        }
                                    }}>
                                    {showUniversityPassword ? universityPassword : '••••••••••'}
                                </S.PasswordValue>
                                <S.PasswordToggle
                                    role="button"
                                    tabIndex={0}
                                    aria-label={showUniversityPassword ? 'Hide password' : 'Show password'}
                                    onClick={() => setShowUniversityPassword((prev) => !prev)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            setShowUniversityPassword((prev) => !prev);
                                        }
                                    }}>
                                    <S.PasswordToggleIcon
                                        path={showUniversityPassword ? mdiEyeOffOutline : mdiEyeOutline}
                                        size={0.75}
                                    />
                                </S.PasswordToggle>
                            {copied && <S.CopiedText>Copied</S.CopiedText>}
                            </S.PasswordValueRow>
                        </S.PasswordRow>
                    </S.ResourceLinks>
                    {demoModeEnabled && (
                        <>
                            <S.DemoModeIndicator>
                                <S.DemoModeText>Demo Mode</S.DemoModeText>
                            </S.DemoModeIndicator>
                        </>
                    )}
                </S.SideBar>
            );
        },
    ),
);
